<template>
  <div class="home">
    <div>
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-4 mb-lg-0">
              <div class="d-flex align-items-center">
                <div>
                  <h4 class="mb-0 fw-bold text-dark">{{$t('reservation_insights')}}</h4>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center gap-3">
              <div class="input-group bg-white rounded-3 border shadow-sm overflow-hidden" style="min-width: 200px;">
                <span class="input-group-text border-0 bg-white pe-0">
                  <AppIcon name="bi-calendar-event" class="bi bi-calendar-event text-muted" />
                </span>
                <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select border-0 shadow-none py-2 ps-2">
                  <option value="">{{$t('filter_by_date')}}</option>
                  <option value="today">{{$t('today')}}</option>
                  <option value="yesterday">{{$t('yesterday')}}</option>
                  <option value="this_week">{{$t('this_week')}}</option>
                  <option value="last_week">{{$t('last_week')}}</option>
                  <option value="this_month">{{$t('this_month')}}</option>
                  <option value="custom">{{$t('custom_date')}}</option>
                </select>
              </div>
              <div v-if="dateRangeOption === 'custom'" class="animate__animated animate__fadeIn">
                <date-picker 
                  :clearable="true" 
                  :editable="false" 
                  class="premium-datepicker" 
                  v-model="dateRange" 
                  type="date" 
                  range 
                  :multi-calendars="1"
                  :placeholder="$t('select_date_range')"
                  @change="fetchInsights"
                ></date-picker>
              </div>
              <button @click="fetchInsights" class="btn btn-white border shadow-sm rounded-3 py-2 px-3" :disabled="loading">
                <AppIcon name="bi-arrow-clockwise" class="bi bi-arrow-clockwise" :class="{'spinner-border-sm': loading}" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="px-8">
        <!-- Insights Cards -->
        <div class="row g-4 mb-6">
          <div class="col-6 col-md-3">
            <div class="card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
              <div class="card-body p-4 border-start border-4 border-primary">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <p class="text-muted small fw-bold text-uppercase mb-1">{{$t('total_reservations')}}</p>
                    <div class="text-muted mb-2" style="font-size: 10px;">{{ formatDate(insights.start_date) }} - {{ formatDate(insights.end_date) }}</div>
                    <h2 class="mb-0 fw-bold">{{ insights.totalReservations || 0 }}</h2>
                  </div>
                  <div class="bg-primary p-2 rounded-3">
                    <AppIcon name="bi-calendar-check" class="bi bi-calendar-check text-white fs-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
              <div class="card-body p-4 border-start border-4 border-warning">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <p class="text-muted small fw-bold text-uppercase mb-1">{{$t('pending_reservations')}}</p>
                    <div class="text-muted mb-2" style="font-size: 10px;">{{ formatDate(insights.start_date) }} - {{ formatDate(insights.end_date) }}</div>
                    <h2 class="mb-0 fw-bold text-warning">{{ insights.pendingReservations || 0 }}</h2>
                  </div>
                  <div class="bg-warning p-2 rounded-3">
                    <AppIcon name="bi-clock" class="bi bi-clock text-white fs-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
              <div class="card-body p-4 border-start border-4 border-success">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <p class="text-muted small fw-bold text-uppercase mb-1">{{$t('confirmed_reservations')}}</p>
                    <div class="text-muted mb-2" style="font-size: 10px;">{{ formatDate(insights.start_date) }} - {{ formatDate(insights.end_date) }}</div>
                    <h2 class="mb-0 fw-bold text-success">{{ insights.confirmedReservations || 0 }}</h2>
                  </div>
                  <div class="bg-success p-2 rounded-3">
                    <AppIcon name="bi-check-circle" class="bi bi-check-circle text-white fs-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-6 col-md-3">
            <div class="card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
              <div class="card-body p-4 border-start border-4 border-danger">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <p class="text-muted small fw-bold text-uppercase mb-1">{{$t('cancelled_reservations')}}</p>
                    <div class="text-muted mb-2" style="font-size: 10px;">{{ formatDate(insights.start_date) }} - {{ formatDate(insights.end_date) }}</div>
                    <h2 class="mb-0 fw-bold text-danger">{{ insights.cancelledReservations || 0 }}</h2>
                  </div>
                  <div class="bg-danger p-2 rounded-3">
                    <AppIcon name="bi-x-circle" class="bi bi-x-circle text-white fs-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Reservations -->
        <div class="row g-4 mb-6">
          <div class="col-md-5">
            <div class="card border-0 shadow-sm rounded-4 h-100">
              <div class="card-header bg-white border-0 py-4 px-4">
                <h5 class="card-title fw-bold text-dark mb-0">{{$t('recent_growth') || 'Recent Growth'}}</h5>
              </div>
              <div class="card-body px-4 pb-4">
                <div class="d-flex justify-content-between align-items-center mb-4">
                  <div class="d-flex align-items-center">
                    <div class="bg-primary p-2 rounded-circle me-3">
                      <AppIcon name="bi-lightning-charge" class="bi bi-lightning-charge text-white" />
                    </div>
                    <span class="text-muted fw-bold">{{$t('last_7_days')}}</span>
                  </div>
                  <span class="badge bg-primary rounded-pill px-3 py-2 fs-6 shadow-sm">{{ insights.recentReservations || 0 }}</span>
                </div>
                <div class="bg-light p-4 rounded-4 mb-3">
                  <div class="d-flex justify-content-between mb-2">
                    <span class="small fw-bold text-muted">{{$t('completion_rate') || 'Rate' }}</span>
                    <span class="small fw-bold text-primary">{{ getProgressPercentage(insights.recentReservations, globalTotal) }}%</span>
                  </div>
                  <div class="progress" style="height: 12px; background-color: rgba(83, 77, 255, 0.1);">
                    <div 
                      class="progress-bar bg-primary shadow-sm rounded-pill" 
                      role="progressbar"
                      :style="{ width: getProgressPercentage(insights.recentReservations, globalTotal) + '%' }"
                      aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"
                    ></div>
                  </div>
                </div>
                <p class="text-muted small mb-0 d-flex align-items-center">
                  <AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />
                  {{ insights.recentReservations || 0 }} {{$t('out_of')}} {{ globalTotal }} {{$t('reservations_recorded') || 'reservations recorded' }}
                </p>
              </div>
            </div>
          </div>

          <div class="col-md-7">
            <div class="card border-0 shadow-sm rounded-4 h-100">
              <div class="card-header bg-white border-0 py-4 px-4">
                <h5 class="card-title fw-bold text-dark mb-1">{{$t('status_distribution') || 'Status Distribution'}}</h5>
                <div class="text-muted small" style="font-size: 11px;">{{ formatDate(insights.start_date) }} - {{ formatDate(insights.end_date) }}</div>
              </div>
              <div class="card-body px-4 pb-4">
                <div class="row g-4">
                  <div class="col-md-4">
                    <div class="status-widget text-center p-3 rounded-4 bg-light border-0 transition-all">
                      <div class="status-icon-circle bg-warning bg-opacity-10 mx-auto mb-3">
                        <AppIcon name="bi-clock" class="bi bi-clock text-warning" />
                      </div>
                      <h6 class="fw-bold mb-1">{{$t('pending')}}</h6>
                      <div class="fs-4 fw-bold text-dark mb-2">{{ insights.pendingReservations || 0 }}</div>
                      <div class="progress mx-auto" style="height: 6px; width: 80%;">
                        <div class="progress-bar bg-warning rounded-pill" :style="{ width: getProgressPercentage(insights.pendingReservations, insights.totalReservations) + '%' }"></div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="status-widget text-center p-3 rounded-4 bg-light border-0 transition-all">
                      <div class="status-icon-circle bg-success bg-opacity-10 mx-auto mb-3">
                        <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" />
                      </div>
                      <h6 class="fw-bold mb-1">{{$t('confirmed')}}</h6>
                      <div class="fs-4 fw-bold text-dark mb-2">{{ insights.confirmedReservations || 0 }}</div>
                      <div class="progress mx-auto" style="height: 6px; width: 80%;">
                        <div class="progress-bar bg-success rounded-pill" :style="{ width: getProgressPercentage(insights.confirmedReservations, insights.totalReservations) + '%' }"></div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="status-widget text-center p-3 rounded-4 bg-light border-0 transition-all">
                      <div class="status-icon-circle bg-danger bg-opacity-10 mx-auto mb-3">
                        <AppIcon name="bi-x-circle" class="bi bi-x-circle text-danger" />
                      </div>
                      <h6 class="fw-bold mb-1">{{$t('cancelled')}}</h6>
                      <div class="fs-4 fw-bold text-dark mb-2">{{ insights.cancelledReservations || 0 }}</div>
                      <div class="progress mx-auto" style="height: 6px; width: 80%;">
                        <div class="progress-bar bg-danger rounded-pill" :style="{ width: getProgressPercentage(insights.cancelledReservations, insights.totalReservations) + '%' }"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Date Range Info -->
        <!-- <div class="row">
          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="card-title">{{$t('date_range')}}</h6>
                    <p class="text-muted mb-0">
                      {{ formatDate(insights.start_date) }} {{$t('to')}} {{ formatDate(insights.end_date) }}
                    </p>
                  </div>
                  <button @click="fetchInsights" class="btn btn-primary btn-sm" :disabled="loading">
                    <AppIcon name="bi-arrow-clockwise" class="bi bi-arrow-clockwise me-2" />
                    {{$t('refresh')}}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div> -->

        <div class="row mt-4">
          <div class="col-12">
            <div class="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div class="card-header bg-white border-0 p-4">
                <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div>
                    <h5 class="card-title fw-bold text-dark mb-1">{{$t('reservations_log') || 'Reservations Log'}}</h5>
                    <p class="text-muted small mb-0">{{$t('quick_view_of_scheduled_bookings') || 'Quick view of scheduled bookings' }}</p>
                  </div>
                  <div class="btn-group rounded-3 shadow-sm overflow-hidden border" role="group">
                    <button 
                      @click="selectDateRange('today')" 
                      class="btn btn-white border-0 px-4 py-2 small fw-bold transition-all" 
                      :class="selectedDateRange === 'today' ? 'bg-primary text-white' : 'text-muted'"
                    >
                      {{$t('today')}}
                    </button>
                    <button 
                      @click="selectDateRange('yesterday')" 
                      class="btn btn-white border-0 px-4 py-2 small fw-bold transition-all" 
                      :class="selectedDateRange === 'yesterday' ? 'bg-primary text-white' : 'text-muted'"
                    >
                      {{$t('yesterday')}}
                    </button>
                    <button 
                      @click="selectDateRange('tomorrow')" 
                      class="btn btn-white border-0 px-4 py-2 small fw-bold transition-all" 
                      :class="selectedDateRange === 'tomorrow' ? 'bg-primary text-white' : 'text-muted'"
                    >
                      {{$t('tomorrow')}}
                    </button>
                  </div>
                </div>
              </div>
              <div class="card-body p-0">
                <table-loading-bar v-if="loadingReservations" class="text-center py-5" />
                <div v-if="!loadingReservations" class="table-responsive">
                  <table class="table mb-0 table-borderless table-striped small">
                    <thead>
                      <tr class="text-secondary">
                        <th class="pt-4 pb-3 px-6">{{$t('id')}}</th>
                        <th class="pt-4 pb-3 px-6">{{$t('date')}}</th>
                        <th class="pt-4 pb-3 px-6">{{$t('time')}}</th>
                        <th class="pt-4 pb-3 px-6">{{$t('client')}}</th>
                        <th class="pt-4 pb-3 px-6">{{$t('status')}}</th>
                        <th class="pt-4 pb-3 px-6">{{$t('notes')}}</th>
                        <th class="pt-4 pb-3 px-6 text-end">{{$t('action')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="reservation in reservationsList" :key="reservation.id" class="align-middle">
                        <td class="py-5 px-6 fw-bold text-dark">#{{ String(reservation.id).substring(0, 8) }}</td>
                        <td class="py-5 px-6">{{ formatDate(reservation.date) }}</td>
                        <td class="py-5 px-6">
                          <span class="bg-light px-2 py-1 rounded-2 text-dark small fw-bold border">
                            {{ reservation.time }}
                          </span>
                        </td>
                        <td class="py-5 px-6">
                          <div class="d-flex align-items-center">
                            <div class="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-2 fw-bold" style="width: 28px; height: 28px; font-size: 11px;">
                              {{ reservation?.client?.name ? reservation.client.name.charAt(0).toUpperCase() : '?' }}
                            </div>
                            <span class="fw-bold">{{ reservation?.client?.name || '-' }}</span>
                          </div>
                        </td>
                        <td class="py-5 px-6">
                          <span :class="statusBadgeClass(reservation.status)" class="badge rounded-pill small px-3">
                            {{ $t(reservation.status) }}
                          </span>
                        </td>
                        <td class="py-5 px-6 text-muted small text-truncate" style="max-width: 150px;">{{ reservation.notes || '-' }}</td>
                        <td class="py-5 px-6 text-end">
                          <button @click="editReservation(reservation)" class="btn btn-sm btn-light rounded-circle shadow-sm me-1" style="width: 32px; height: 32px;">
                            <AppIcon name="bi-pencil-fill" class="bi bi-pencil-fill text-primary small" />
                          </button>
                          <button @click="deleteReservation(reservation)" class="btn btn-sm btn-light rounded-circle shadow-sm" style="cursor: pointer; padding: 0; width: 32px; height: 32px;">
                            <AppIcon name="bi-trash-fill" class="bi bi-trash-fill text-danger small" />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="!loadingReservations && !reservationsList.length" class="text-center py-5">
                  <div class="mb-3">
                    <AppIcon name="bi-calendar-x" class="bi bi-calendar-x text-muted" style="font-size: 3rem;" />
                  </div>
                  <h5 class="text-muted">{{$t('no_reservations_for_selected_date')}}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import helper from '../helper';

export default {
  name: 'ReservationInsights',
  persistedFilterState: {
    key: 'reservation-insights',
    fields: ['selectedDateRange', 'dateRangeOption', 'dateRange'],
    dateFields: ['dateRange'],
  },
  components: { DatePicker },
  data() {
    return {
      loading: false,
      loadingReservations: false,
      selectedDateRange: 'today',
      reservationsList: [],
      insights: {
        totalReservations: 0,
        pendingReservations: 0,
        confirmedReservations: 0,
        cancelledReservations: 0,
        recentReservations: 0,
        start_date: '',
        end_date: ''
      },
      dateRange: [
        new Date(new Date().setDate(new Date().getDate() - 30)),
        new Date()
      ],
      dateRangeOption: '',
      globalTotal: 0
    };
  },
  computed: {
    helper() { return helper }
  },
  mounted() {
    window.mixpanel.track("reservation_insights");
    // Check permissions
    if(helper.checkAccess('view_reservations') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_reservations') == -2) return this.$router.push({path : '/subscribe'})

    // Initialize this_month date range
    // this.handleDateRangeChange();

    this.fetchInsights();
    this.fetchReservationsList();
    this.fetchGlobalTotal();
  },
  methods: {
    async fetchInsights() {
      try {
        this.loading = true;
        const start = this.dateRange[0] ? helper.formatDate(this.dateRange[0]) : '';
        const end = this.dateRange[1] ? helper.formatDate(this.dateRange[1]) : '';
        
        let url = `${process.env.VUE_APP_API}/reservations/insights`;
        const params = [];
        if (start) {
          params.push(`start=${start}`);
        }
        if (end) {
          params.push(`end=${end}`);
        }
        if (params.length) {
          url += `?${params.join('&')}`;
        }
        
        const response = await helper.customGet(url);
        this.insights = response.data;
      } catch (error) {
        alert('Error fetching insights:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchGlobalTotal() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/reservations/insights`);
        this.globalTotal = response.data.totalReservations || 0;
      } catch (error) {
        alert('Error fetching global total:', error);
      }
    },
    formatDate(date) {
      if (!date) return '';
      return helper.formatDate(date);
    },
    statusBadgeClass(status) {
      return {
        'bg-success bg-opacity-10 text-success': status === 'confirmed',
        'bg-warning bg-opacity-10 text-warning': status === 'pending',
        'bg-danger bg-opacity-10 text-danger': status === 'cancelled',
        'bg-secondary bg-opacity-10 text-secondary': !['confirmed', 'pending', 'cancelled'].includes(status)
      };
    },
    getProgressPercentage(value, total) {
      if (!total || total === 0) return 0;
      return Math.round((value / total) * 100);
    },
    selectDateRange(range) {
      this.selectedDateRange = range;
      this.fetchReservationsList();
    },
    async fetchReservationsList() {
      try {
        this.loadingReservations = true;
        let targetDate = new Date();
        
        switch (this.selectedDateRange) {
          case 'yesterday':
            targetDate.setDate(targetDate.getDate() - 1);
            break;
          case 'tomorrow':
            targetDate.setDate(targetDate.getDate() + 1);
            break;
          case 'today':
          default:
            // Use current date
            break;
        }
        
        const dateStr = helper.formatDate(targetDate);
        const url = `${process.env.VUE_APP_API}/reservations?start=${dateStr}&end=${dateStr}&per_page=100`;
        
        const response = await helper.customGet(url);
        this.reservationsList = response.data.data || response.data || [];
      } catch (error) {
        alert('Error fetching reservations list:', error);
        this.reservationsList = [];
      } finally {
        this.loadingReservations = false;
      }
    },
    formatTime(time) {
      if (!time) return '-';
      const date = new Date(time);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    },
    statusClass(status) {
      return {
        'badge': true,
        'badge active': status === 'confirmed',
        'badge warning': status === 'pending',
        'badge danger': status === 'cancelled',
        'badge expired': !['confirmed', 'pending', 'cancelled'].includes(status)
      };
    },
    editReservation(reservation) {
      // Navigate to reservations page with edit mode
      this.$router.push({ 
        path: '/reservations',
        query: { 
          edit: reservation.id,
          date: helper.formatDate(reservation.date)
        }
      });
    },
    async deleteReservation(reservation) {
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      });
      
      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/reservations/${reservation.id}`);
          this.reservationsList = helper.withoutDeletedRecords(this.reservationsList, reservation.id);
          this.fetchInsights(); // Refresh insights
        } catch (error) {
          alert('Error deleting reservation:', error);
        }
      }
    },
    // Handle date range selection
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        this.dateRange = []
        this.fetchInsights()
      } else if (this.dateRangeOption === 'today') {
        const start = new Date()
        const end = new Date()
        this.dateRange = [start, end]
      } else if (this.dateRangeOption === 'yesterday') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24)
        end.setTime(end.getTime() - 3600 * 1000 * 24)
        this.dateRange = [start, end]
      } else if (this.dateRangeOption === 'this_week') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
        this.dateRange = [start, end]
      } else if (this.dateRangeOption === 'last_week') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 14)
        end.setTime(end.getTime() - 3600 * 1000 * 24 * 7)
        this.dateRange = [start, end]
      } else if (this.dateRangeOption === 'this_month') {
        const start = new Date()
        const end = new Date()
        start.setDate(1)
        this.dateRange = [start, end]
      } else if (this.dateRangeOption === 'custom') {
        this.dateRange = []
      }
      
      if (this.dateRangeOption !== 'custom') {
        this.fetchInsights();
        // If it's a specific day (today/yesterday), update the table too
        if (['today', 'yesterday'].includes(this.dateRangeOption)) {
          this.selectedDateRange = this.dateRangeOption;
          this.fetchReservationsList();
        }
      }
    }
  }
};
</script>

<style scoped>/* Premium Styles */
.card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.status-widget {
  transition: all 0.3s ease;
}

.status-widget:hover {
  background-color: #fff !important;
  box-shadow: 0 10px 20px rgba(0,0,0,0.05);
  transform: translateY(-5px);
}

.status-icon-circle {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 1.25rem;
}

.progress {
  background-color: #f0f2f5;
  border-radius: 100px;
}

.progress-bar {
  border-radius: 100px;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

.transition-all {
  transition: all 0.3s ease;
}

.rounded-4 {
  border-radius: 1rem !important;
}

.premium-datepicker {
  width: 100%;
}

.premium-datepicker >>> .mx-input {
  border-radius: 0.5rem;
  border-color: #e2e8f0;
  box-shadow: none;
}

.btn-white {
  background-color: #fff;
  color: #344767;
}

.btn-white:hover {
  background-color: #f8fafc;
  color: #344767;
}

.badge.active {
  background: #198754;
  color: white;
}

.badge.expired {
  background: #FEE2E2;
  color: #b91c1c;
}

.badge.danger {
  background: #dc3545;
  color: white;
}

.badge.warning {
  background: #FEF3C7;
  color: #92400E;
}

.table thead th {
  border-bottom: none;
}

.table tbody td {
  border-color: #f8fafc;
}

.table-borderless>:not(caption)>*>*{
    padding: 12px 24px !important;
}

.text-primary {
  color: #303030 !important;
}

.bg-primary {
  background-color: #004aad !important;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate__animated {
  animation-duration: 0.5s;
  animation-fill-mode: both;
}

.animate__fadeIn {
  animation-name: fadeIn;
}
</style>
